/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Kafka (Apache, Confluent, & Redpanda)", description = "Hightouch integration with Apache Kafka, Confluent Cloud, and Redpanda", lastUpdatedDate = new Date(1758844800000), icon = "https://simpleicons.org/icons/apachekafka.svg";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Kafka (Apache, Confluent, & Redpanda)",
        description: "Hightouch integration with Apache Kafka, Confluent Cloud, and Redpanda",
        lastUpdatedDate: "Fri Sep 26 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "kafka",
        type: "source",
        icon: "https://simpleicons.org/icons/apachekafka.svg",
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Message format",
          "depth": 1,
          "id": "message-format"
        }, {
          "value": "Event Structure",
          "depth": 0,
          "id": "event-structure"
        }, {
          "value": "Field mappings",
          "depth": 1,
          "id": "field-mappings"
        }, {
          "value": "Schema Enforcement",
          "depth": 1,
          "id": "schema-enforcement"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      img: "img",
      h3: "h3",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports streaming events from ", _jsx(_components.strong, {
          children: "Apache Kafka"
        }), ", ", _jsx(_components.strong, {
          children: "Confluent Cloud"
        }), ", and ", _jsx(_components.strong, {
          children: "Redpanda"
        }), " clusters in real time."]
      }), "\n", _jsxs(_components.p, {
        children: ["All three are ", _jsx(_components.strong, {
          children: "Kafka-compatible platforms"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Apache Kafka"
          }), ": the open-source distributed event streaming platform."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Confluent Cloud"
          }), ": a fully managed Kafka-as-a-service offering with enterprise features."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Redpanda"
          }), ": a high-performance, Kafka API–compatible platform designed as a drop-in replacement."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Since they all speak the ", _jsx(_components.strong, {
          children: "Kafka protocol"
        }), ", setup and event ingestion in Hightouch are nearly identical."]
      }), "\n", _jsx(_components.p, {
        children: "Once connected, Hightouch automatically extracts, transforms, and loads your event data into Hightouch Events. Your data can then be synced to your warehouse or streamed to our catalog of real-time destinations."
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, ", _jsx(_components.a, {
          href: "https://app.hightouch.com/events/sources/new?from=docs",
          children: _jsx(_components.strong, {
            children: "create an event source"
          })
        }), " and select either:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Kafka"
          }), " for Apache Kafka"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Confluent"
          }), " for Confluent Cloud"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Redpanda"
          }), " (for Redpanda clusters)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Both sources use the same configuration flow."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/kafka-confluent.png",
          alt: "Select a source"
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Kafka"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "The required fields are the same across Apache Kafka, Confluent, and Redpanda:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Broker URL"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Kafka: hostname:port for your brokers."
            }), "\n", _jsx(_components.li, {
              children: "Confluent: provided in your Confluent cluster settings."
            }), "\n", _jsx(_components.li, {
              children: "Redpanda: hostname:port for your Redpanda brokers."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Topic"
          }), ": The topic to consume events from. Messages must be JSON-formatted."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Authentication"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Kafka: Multiple auth methods supported."
            }), "\n", _jsx(_components.li, {
              children: "Confluent: Use SASL/SSL credentials from Confluent Cloud."
            }), "\n", _jsx(_components.li, {
              children: "Redpanda: Uses Kafka-compatible auth methods configured in your cluster."
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "message-format",
        children: "Message format"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch Events requires Kafka messages be encoded as JSON."
      }), "\n", _jsx(_components.h2, {
        id: "event-structure",
        children: "Event Structure"
      }), "\n", _jsxs(_components.p, {
        children: ["All events from Kafka, Confluent, and Redpanda are ingested into Hightouch as ", _jsx(_components.code, {
          children: "track"
        }), " events."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Message ", _jsx(_components.code, {
            children: "key"
          }), ", ", _jsx(_components.code, {
            children: "value"
          }), ", and ", _jsx(_components.code, {
            children: "headers"
          }), " → ", _jsx(_components.code, {
            children: "properties"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Metadata such as ", _jsx(_components.code, {
            children: "topic"
          }), ", ", _jsx(_components.code, {
            children: "partition"
          }), ", and ", _jsx(_components.code, {
            children: "offset"
          }), " → ", _jsx(_components.code, {
            children: "context.kafka"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For example, the following Kafka message:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"partition\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"offset\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"timestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1721659050648"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"key\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"k1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123.45"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"headers\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"key\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"h1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"abc\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Will be ingested into Hightouch as:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Kafka Event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"key\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"k1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123.45"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"headers\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"h1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"abc\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"context\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"kafka\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"topic\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"my-topic\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"partition\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"offset\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"timestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2024-07-22T14:52:37.648Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can configure how Hightouch transforms messages from your Kafka topic to Hightouch ", _jsxs(_components.a, {
          href: "/events/event-spec#track-events",
          children: [_jsx(_components.code, {
            children: "track"
          }), " events"]
        }), ". Mappings can be configured for the following fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "event"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "userId"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "anonymousId"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "messageId"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "timestamp"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can also use Functions to standardize schemas or enrich event data."
      }), "\n", _jsx(_components.h3, {
        id: "schema-enforcement",
        children: "Schema Enforcement"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management",
          children: "Event Contracts"
        }), " can be attached to Kafka, Confluent, and Redpanda sources just like any other Event Source."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
